import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../../../Components/Atoms/Modal";
import { useOnboardingStore } from "../../../../Store/Onboarding";
import { capitalizeAfterSpace } from "../../../../Contracts/Common";
import { Customer, PaymentsGrowCustomer } from "../../../../Contracts/Customer";
import { useAuth0 } from "@auth0/auth0-react";
import { useCustomerStore } from "../../../../Store/Customer";
import { DropdownOption } from "../../../../Components/Atoms/Dropdown";
import Select from "../../../../Components/Atoms/Select";
import { ReactComponent as InfoIcon } from "../../../../Assets/ActionIcons/InfoIcon.svg";

enum MappingSteps {
  SelectClient = "select_client",
  ConfirmClient = "confirm_client"
}

export type SyncUnmappedCustomerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedCustomer?: PaymentsGrowCustomer;
};

const SyncUnmappedCustomerModal: React.FC<SyncUnmappedCustomerModalProps> = ({
  isOpen,
  onClose,
  selectedCustomer
}) => {
  const { adyenKYCDetails } = useOnboardingStore();
  const { getAccessTokenSilently } = useAuth0();
  const { getCustomers, customers, mapGrowCustomer } = useCustomerStore();
  const [selectedIntegrationCustomer, setSelectedIntegrationCustomer] = React.useState<
    Customer | undefined
  >();
  const [confirmedIntegrationCustomer, setConfirmedIntegrationCustomer] = React.useState<
    Customer | undefined
  >();
  const [showValidationError, setShowValidationError] = React.useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<MappingSteps>(MappingSteps.SelectClient);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const refreshData = useCallback(async () => {
    await getCustomers(getAccessTokenSilently, undefined, undefined, undefined, undefined, search);
  }, [getAccessTokenSilently, getCustomers, search]);

  useEffect(() => {
    refreshData();
  }, [isOpen, refreshData, search]);

  useEffect(() => {
    if (isOpen) {
      setSearch(undefined);
      setSelectedIntegrationCustomer(undefined);
      setConfirmedIntegrationCustomer(undefined);
      setCurrentStep(MappingSteps.SelectClient);
    }
  }, [isOpen]);

  const syncUnmappedCustomerConfirmed = async () => {
    if (selectedCustomer && selectedIntegrationCustomer) {
      await mapGrowCustomer(
        getAccessTokenSilently,
        selectedCustomer.id,
        selectedIntegrationCustomer.id
      );
      onClose();
    }
  };

  const onOk = () => {
    if (currentStep === MappingSteps.SelectClient) {
      if (selectedIntegrationCustomer) {
        setCurrentStep(MappingSteps.ConfirmClient);
        setShowValidationError(false);
      } else {
        setShowValidationError(true);
      }
    } else {
      if (selectedIntegrationCustomer?.id !== confirmedIntegrationCustomer?.id) {
        setShowValidationError(true);
      } else {
        setShowValidationError(false);
        syncUnmappedCustomerConfirmed();
      }
    }
  };

  const dropDownOptions: DropdownOption[] = customers.map(c => ({
    label: c.name,
    value: c.id
  }));

  return (
    <Modal
      title={`Sync Client in ${capitalizeAfterSpace(adyenKYCDetails?.connected_integration)}`}
      isOpen={isOpen}
      onClose={onClose}
      topOffset="10rem"
      size="medium"
      cancelText="Previous"
      onOk={onOk}
      okText={currentStep === MappingSteps.SelectClient ? "Next" : "Confirm and Sync"}
      hideCancelButton={currentStep === MappingSteps.SelectClient}
    >
      {currentStep === MappingSteps.SelectClient ? (
        <>
          <div>
            Sync payment methods to clients in{" "}
            {capitalizeAfterSpace(adyenKYCDetails?.connected_integration)} for efficient financial
            management. Simplify your workflow and gain better control over your transactions.
          </div>
          <div className="grid grid-cols-5 gap-2 items-center mt-3">
            <label className="col-span-1">Client Name</label>
            <div className="border border-gray-300 rounded w-full p-2 col-span-4">
              {selectedCustomer?.client_company_name || selectedCustomer?.customer_name}
            </div>
            <label className="col-span-1">Sync With</label>
            <Select
              dropDownOptions={dropDownOptions}
              onChange={e => {
                const selectedCustomer = customers.find(c => c.id === e);
                setSelectedIntegrationCustomer(selectedCustomer);
                setShowValidationError(false);
              }}
              onSearch={setSearch}
              searchWithDebounce={true}
              className="col-span-4"
            />
            {showValidationError && (
              <>
                <div className="col-span-1" />
                <div className="col-span-4 text-red-500">Please select a client to sync with</div>
              </>
            )}
          </div>
        </>
      ) : (
        <div>
          <div className="bg-yellow-50 border border-yellow-300 rounded p-4 mb-4 flex space-x-2">
            <InfoIcon className="w-5 h-5 text-yellow-600" />
            <div>
              This action will sync{" "}
              {selectedCustomer?.client_company_name || selectedCustomer?.customer_name} payment
              details with customer {selectedIntegrationCustomer?.name} in{" "}
              {capitalizeAfterSpace(adyenKYCDetails?.connected_integration)} and will be used for
              making future payments.
            </div>
          </div>
          <div>
            Please select the {capitalizeAfterSpace(adyenKYCDetails?.connected_integration)} client
            once more to confirm
          </div>
          <div className="grid grid-cols-5 gap-2 items-center mt-3">
            <label className="col-span-1">Sync With</label>
            <Select
              dropDownOptions={dropDownOptions}
              onChange={e => {
                const selectedCustomer = customers.find(c => c.id === e);
                setConfirmedIntegrationCustomer(selectedCustomer);
                setShowValidationError(false);
              }}
              onSearch={setSearch}
              searchWithDebounce={true}
              className="col-span-4"
            />
            {showValidationError && (
              <>
                <div className="col-span-1" />
                <div className="col-span-4 text-red-500">
                  {!confirmedIntegrationCustomer
                    ? "Please select a client to sync with"
                    : "Selected client does not match. Please select the correct client"}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SyncUnmappedCustomerModal;
